import React, { useEffect, useState } from 'react';
import { X, Printer, MapPin } from 'lucide-react';
import QRCode from 'qrcode';
import { useI18n } from '@/shared/i18n';
import { Button } from '@/core/ui/components/Button';
import type { CheckpointQRResponse } from '@yawi/types';

interface CheckpointQRModalProps {
  qrData: CheckpointQRResponse;
  location?: string | null;
  orderIndex: number;
  onClose: () => void;
}

export const CheckpointQRModal: React.FC<CheckpointQRModalProps> = ({
  qrData,
  location,
  orderIndex,
  onClose,
}) => {
  const { t } = useI18n();
  const [qrUrl, setQrUrl] = useState('');

  useEffect(() => {
    const generate = async () => {
      try {
        const url = await QRCode.toDataURL(qrData.qrContent, {
          width: 300,
          margin: 2,
          color: {
            dark: '#1e293b',
            light: '#ffffff',
          },
        });
        setQrUrl(url);
      } catch (err) {
        console.error('Error generating QR:', err);
      }
    };
    generate();
  }, [qrData.qrContent]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in print:bg-white print:p-0">
      <div className="bg-white w-full max-w-sm rounded-2xl p-8 text-center relative shadow-2xl print:shadow-none print:rounded-none print:max-w-none print:w-auto">
        {/* Close button - hidden on print */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 print:hidden"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-widest mb-1 print:text-sm">
            {t('patrol.checkpoint')}
          </p>
          <h3 className="text-xl font-bold text-slate-800 print:text-2xl">
            {qrData.name}
          </h3>
        </div>

        {/* QR Code */}
        <div className="bg-white p-3 border-4 border-slate-900 rounded-xl inline-block mb-6 print:border-2">
          {qrUrl ? (
            <img src={qrUrl} alt="QR Code" className="w-64 h-64 print:w-72 print:h-72" />
          ) : (
            <div className="w-64 h-64 bg-slate-100 animate-pulse rounded-lg" />
          )}
        </div>

        {/* Info */}
        <div className="space-y-2 mb-6">
          {location && (
            <p className="text-sm text-slate-600 flex items-center justify-center gap-2">
              <MapPin size={14} className="text-slate-400" />
              {location}
            </p>
          )}
          <p className="text-xs text-slate-400">
            #{orderIndex} en ruta de rondín
          </p>
        </div>

        {/* QR Code reference - smaller */}
        <p className="text-[10px] font-mono text-slate-300 mb-6 break-all print:text-xs print:text-slate-500">
          {qrData.qrCode}
        </p>

        {/* Print button - hidden on print */}
        <Button
          onClick={handlePrint}
          icon={<Printer size={16} />}
          className="w-full print:hidden"
        >
          {t('common.print')}
        </Button>
      </div>
    </div>
  );
};
